import React from 'react';
import classNames from 'classnames';

import classes from './index.module.css';

enum Aligns {
  LEFT = 'start',
  RIGHT = 'end',
  CENTER = 'center',
}

interface ZpButtonProps {
  theme?: 'primary' | 'secondary' | 'super-primary' | 'link' | 'danger';
  size?: 'big' | 'small';
  type?: 'button' | 'submit' | 'reset';
  staticWidth?: null | string | number;
  minWidth?: number | string;
  icon?: null | string | React.ReactNode;
  isLeftIcon?: boolean;
  dropdownStatus?: null | '' | 'open' | 'closed';
  className?: string;
  isFullWidth?: boolean;
  isAutoWidth?: boolean;
  textNowrap?: boolean;
  align?: `${Aligns}`;
  children?: React.ReactNode;
  onClick?: () => void;
  withContent?: boolean;
  [prop: string]: unknown;
}

const defaultProps: ZpButtonProps = {
  theme: 'primary',
  size: 'small',
  minWidth: null,
  staticWidth: null,
  icon: null,
  isLeftIcon: true,
  dropdownStatus: null,
  className: '',
  isFullWidth: false,
  isAutoWidth: false,
  textNowrap: false,
  children: null,
  align: 'center',
  withContent: true,
};

const ZpButton: React.FC<ZpButtonProps> = ({
  theme,
  children,
  size,
  staticWidth,
  minWidth,
  icon,
  isLeftIcon,
  dropdownStatus,
  className,
  isFullWidth,
  isAutoWidth,
  textNowrap,
  align,
  withContent,
  ...restButtonConfig
}: ZpButtonProps) => {
  const combinedClasses = classNames(
    classes.button,
    classes[theme],
    classes[size],
    {
      [classes['btn-link']]: theme === 'link',
      [classes['with-icon']]: icon,
      [classes['with-right-icon']]: (icon || dropdownStatus) && (!isLeftIcon || dropdownStatus),
      [classes['with-left-icon']]: (icon || dropdownStatus) && isLeftIcon && !dropdownStatus,
      [classes['btn-dropdown']]: dropdownStatus,
      [classes['dropdown-open']]: dropdownStatus === 'open',
      [classes['full-width']]: isFullWidth,
      [classes['auto-width']]: isAutoWidth,
      'text-left': align === Aligns.LEFT,
      'text-right': align === Aligns.RIGHT,
    },
    className
  );

  return (
    <button
      data-testid="zp-button"
      type="button"
      style={{ width: staticWidth, minWidth: minWidth || staticWidth, maxWidth: staticWidth }}
      className={combinedClasses}
      {...restButtonConfig}
    >
      <div
        className={classNames(classes.content, {
          [classes['dropdown-content']]: dropdownStatus,
          'justify-content-start': align === Aligns.LEFT,
          'justify-content-end': align === Aligns.RIGHT,
        })}
      >
        {isLeftIcon && icon && (
          <div data-testid="with-left-icon" className={`${classes.content} ${classes.icon} ${classes['icon-left']}`}>{icon}</div>
        )}
        {withContent && (
          <div data-testid="with-content"  className={classNames(classes.text, { 'text-nowrap': textNowrap })}>{children}</div>
        )}
        {!isLeftIcon && icon && (
          <div data-testid="with-right-icon"  className={`${classes.content} ${classes.icon} ${classes['icon-right']}`}>
            {icon}
          </div>
        )}
        {!withContent && children}
      </div>
    </button>
  );
};

ZpButton.defaultProps = defaultProps;

export default ZpButton;
